import React, { useState, useEffect } from 'react';
import { tenantService } from '../../services/tenantService';
import { packageService } from '../../services/packageService';
import { Tenant, UpdateTenantRequest } from '../../types/tenant';
import { Package } from '../../types/package';
import { useToast } from '../../contexts/ToastContext';

interface EditBrandModalProps {
  isOpen: boolean;
  brand: Tenant | null;
  onClose: () => void;
  onSuccess: () => void;
}

const EditBrandModal: React.FC<EditBrandModalProps> = ({ isOpen, brand, onClose, onSuccess }) => {
  const { toast } = useToast();
  const [name, setName] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [selectedPackageId, setSelectedPackageId] = useState<string>('');
  const [packages, setPackages] = useState<Package[]>([]);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (brand) {
      setName(brand.name);
      setSubdomain(brand.subdomain);
      setSelectedPackageId(brand.packageId || '');
    }
  }, [brand]);

  useEffect(() => {
    if (isOpen && packages.length === 0) {
      setPackagesLoading(true);
      packageService
        .getAll()
        .then((res) => {
          if (res.success) setPackages(res.data.filter((p) => p.isActive));
        })
        .catch(() => {})
        .finally(() => setPackagesLoading(false));
    }
  }, [isOpen, packages.length]);

  const handleSave = async () => {
    if (!brand || !name.trim() || !subdomain.trim()) return;
    setIsSubmitting(true);
    try {
      const request: UpdateTenantRequest = {
        name: name.trim(),
        subdomain: subdomain.trim(),
      };
      const res = await tenantService.update(brand.id, request);
      if (res.success) {
        // If package changed, assign the new package
        if (selectedPackageId !== (brand.packageId || '')) {
          await tenantService.assignPackage(brand.id, {
            packageId: selectedPackageId || null,
          });
        }
        toast.success('Brand updated successfully');
        onSuccess();
        onClose();
      } else {
        toast.error(res.message || 'Failed to update brand');
      }
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { message?: string } } };
      toast.error(axiosError.response?.data?.message || 'Failed to update brand');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !brand) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4">
      <div
        className="bg-white w-full max-w-lg rounded-2xl border border-slate-200 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-8 pt-8 pb-4 flex items-center justify-between">
          <h2 className="text-xl font-black tracking-tight">Edit Brand</h2>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="text-slate-500 hover:text-slate-900 transition-colors disabled:opacity-50"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Content */}
        <div className="px-8 pb-6 space-y-5">
          <div className="space-y-2">
            <label className="text-xs font-bold text-slate-500">Brand Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-slate-50 border-slate-200 rounded-lg py-3 px-4 text-sm focus:ring-1 focus:ring-primary focus:border-primary transition-all text-slate-700"
            />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-bold text-slate-500">Subdomain</label>
            <input
              type="text"
              value={subdomain}
              onChange={(e) => setSubdomain(e.target.value)}
              className="w-full bg-slate-50 border-slate-200 rounded-lg py-3 px-4 text-sm focus:ring-1 focus:ring-primary focus:border-primary transition-all text-slate-700"
            />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-bold text-slate-500">Package</label>
            {packagesLoading ? (
              <div className="w-full bg-slate-50 rounded-lg py-3 px-4 text-sm text-slate-500">
                Loading packages...
              </div>
            ) : (
              <select
                value={selectedPackageId}
                onChange={(e) => setSelectedPackageId(e.target.value)}
                className="w-full bg-slate-50 border-slate-200 rounded-lg py-3 px-4 text-sm focus:ring-1 focus:ring-primary focus:border-primary transition-all appearance-none text-slate-600"
              >
                <option value="">No Package</option>
                {packages.map((pkg) => (
                  <option key={pkg.id} value={pkg.id}>
                    {pkg.name} - {pkg.monthlyPrice > 0 ? `${pkg.monthlyPrice} ${pkg.currency}/mo` : 'Free'}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 py-5 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-3 rounded-b-2xl">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="px-6 py-2.5 text-xs font-bold text-slate-500 hover:text-slate-900 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSubmitting || !name.trim() || !subdomain.trim()}
            className="bg-primary hover:bg-primary/90 text-white px-8 py-2.5 rounded-lg font-black text-xs uppercase tracking-widest transition-all shadow-lg shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditBrandModal;
